import React, { useState, useEffect } from 'react'

/**
 * PollViewer — renders an interactive poll widget inside a published blog.
 *
 * Props:
 *   pollId   {string}  — stable, unique identifier for localStorage key scoping
 *   question {string}  — poll question text
 *   options  {Array}   — [{ uid, text, votes }]  (votes = initial count from export)
 */
export default function PollViewer({ pollId, question, options: initialOptions }) {
  // Derive per-poll localStorage key
  const storageKey = `poll_${pollId}`

  // Initialise vote counts from localStorage if the user has already voted,
  // otherwise fall back to the counts baked in at export time.
  const [options, setOptions] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null')
      if (saved?.options) return saved.options
    } catch {}
    return initialOptions.map((o) => ({ ...o }))
  })

  const [votedUid, setVotedUid] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null')
      return saved?.votedUid ?? null
    } catch {}
    return null
  })

  const [selected, setSelected] = useState(null)
  const hasVoted = votedUid !== null

  const totalVotes = options.reduce((sum, o) => sum + o.votes, 0)

  // Persist vote state whenever it changes
  useEffect(() => {
    if (hasVoted) {
      try {
        localStorage.setItem(storageKey, JSON.stringify({ votedUid, options }))
      } catch {}
    }
  }, [votedUid, options, storageKey, hasVoted])

  const handleVote = () => {
    if (!selected || hasVoted) return
    const updated = options.map((o) =>
      o.uid === selected ? { ...o, votes: o.votes + 1 } : o
    )
    setOptions(updated)
    setVotedUid(selected)
    setSelected(null)
  }

  return (
    <div
      className="PollViewer__container"
      style={{
        maxWidth: '100%',
        padding: '12px 16px',
        margin: '12px 0',
      }}
    >
      <h2 className="PollViewer__question">{question}</h2>

      <div className="PollViewer__options">
        {options.map((option) => {
          const pct = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0
          const isVoted = votedUid === option.uid
          const isSelected = selected === option.uid

          return (
            <div
              key={option.uid}
              className={[
                'PollViewer__option',
                hasVoted ? 'PollViewer__option--result' : 'PollViewer__option--selectable',
                isVoted ? 'PollViewer__option--voted' : '',
                !hasVoted && isSelected ? 'PollViewer__option--selected' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => {
                if (!hasVoted) setSelected(isSelected ? null : option.uid)
              }}
              role={hasVoted ? undefined : 'radio'}
              aria-checked={isSelected ? 'true' : 'false'}
              tabIndex={hasVoted ? undefined : 0}
              onKeyDown={(e) => {
                if (!hasVoted && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  setSelected(isSelected ? null : option.uid)
                }
              }}
            >
              {/* Progress bar shown after voting */}
              {hasVoted && (
                <div
                  className="PollViewer__bar"
                  style={{ width: `${pct}%` }}
                  aria-hidden="true"
                />
              )}

              <div className="PollViewer__optionContent">
                {!hasVoted && (
                  <span
                    className={`PollViewer__radio ${isSelected ? 'PollViewer__radio--checked' : ''}`}
                    aria-hidden="true"
                  />
                )}
                <span className="PollViewer__optionText">{option.text}</span>
                {hasVoted && (
                  <span className="PollViewer__pct">
                    {pct}%{isVoted && <span className="PollViewer__checkmark"> ✓</span>}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {!hasVoted && (
        <button
          className="PollViewer__voteBtn"
          onClick={handleVote}
          disabled={!selected}
        >
          Vote
        </button>
      )}

      <p className="PollViewer__meta">
        {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
        {hasVoted && ' · Thanks for voting!'}
      </p>
    </div>
  )
}
